import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  IconButton,
  Divider,
  Stack,
  useTheme,
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  Brightness4,
  Work,
  Description,
  Dashboard,
  Info,
} from '@mui/icons-material';

const Footer = () => {
  const theme = useTheme();

  const quickLinks = [
    { text: 'Create CV', icon: <Description />, path: '/generate' },
    { text: 'About Us', icon: <Info />, path: '/about' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, #1a237e 100%)`,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      {/* Wave decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '10px',
          background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4)',
        }}
      />
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 6 }}>
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="h4" 
                gutterBottom 
                sx={{ 
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #fff, #e3f2fd)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                CleverCV
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontStyle: 'italic', 
                  opacity: 0.9,
                  mb: 2,
                  fontSize: '1.1rem'
                }}
              >
                Because why settle for a mediocre CV?
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8, lineHeight: 1.6 }}>
                Transform your career with professionally crafted CVs that make you stand out from the crowd. 
                AI-powered, recruiter-approved, success-guaranteed.
              </Typography>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} md={2.5}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
              Quick Links
            </Typography>
            <Stack spacing={1.5}>
              {quickLinks.map((link, index) => (
                <Box
                  key={index}
                  component={Link}
                  to={link.path}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'inherit',
                    textDecoration: 'none',
                    padding: '8px 0',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: theme.palette.secondary.main,
                      transform: 'translateX(8px)',
                      '& .MuiSvgIcon-root': {
                        transform: 'scale(1.1)',
                      },
                    },
                  }}
                >
                  <Box sx={{ mr: 1.5, display: 'flex', transition: 'transform 0.2s ease' }}>
                    {link.icon}
                  </Box>
                  <Typography variant="body1">{link.text}</Typography>
                </Box>
              ))}
            </Stack>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} md={3}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
              Contact Us
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Email sx={{ mr: 2, color: theme.palette.secondary.main }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Email
                  </Typography>
                  <Typography 
                    variant="body2" 
                    component="a" 
                    href="mailto:sherry@friendlymeerkat.co.za"
                    sx={{ 
                      color: 'inherit', 
                      textDecoration: 'none',
                      '&:hover': { color: theme.palette.secondary.main }
                    }}
                  >
                    sherry@friendlymeerkat.co.za
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Phone sx={{ mr: 2, color: theme.palette.secondary.main }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Phone
                  </Typography>
                  <Typography variant="body2">
                    +27 82 064 6741
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <LocationOn sx={{ mr: 2, mt: 0.5, color: theme.palette.secondary.main }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Address
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                    18 St Johns Road,<br />
                    Houghton Estate, Sandton
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Grid>

          {/* Newsletter & Legal */}
          <Grid item xs={12} md={2.5}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
              Stay Updated
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
              Get the latest CV tips and career advice directly to your inbox.
            </Typography>
            <Box
              sx={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                padding: 2,
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.2)',
                mb: 3,
              }}
            >
              <Typography variant="body2" sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                Newsletter coming soon!
              </Typography>
            </Box>
            
            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
              Legal
            </Typography>
            <Stack spacing={1}>
              <Link 
                to="/privacy-policy" 
                style={{ 
                  color: 'inherit', 
                  textDecoration: 'none',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = theme.palette.secondary.main}
                onMouseLeave={(e) => e.target.style.color = 'inherit'}
              >
                <Typography variant="body2">Privacy Policy</Typography>
              </Link>
              <Link 
                to="/terms-of-service" 
                style={{ 
                  color: 'inherit', 
                  textDecoration: 'none',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = theme.palette.secondary.main}
                onMouseLeave={(e) => e.target.style.color = 'inherit'}
              >
                <Typography variant="body2">Terms of Service</Typography>
              </Link>
            </Stack>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider sx={{ my: 4, backgroundColor: 'rgba(255,255,255,0.2)' }} />

        {/* Bottom Section */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            © {new Date().getFullYear()} CleverCV. All rights reserved. Made with ❤️ for your career success.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Powered by AI
            </Typography>
            <Brightness4 sx={{ fontSize: '1rem', opacity: 0.7 }} />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 